import { AppError } from '@/utils/api';
import { invalidateAllProductCaches, registerProductCacheInvalidator } from '@/services/products-cache';
import { AuthenticatedHttp, GuestHttp } from '../../http/axios';

const productsCache = new Map();

function normalizeValue(v) {
  if (Array.isArray(v)) return [...v].sort();
  if (v === undefined || v === null) return '';
  return v;
}

function buildKey(params) {
  const normalized = {};
  Object.keys(params || {})
    .sort()
    .forEach((k) => {
      normalized[k] = normalizeValue(params[k]);
    });
  return JSON.stringify(normalized);
}

function getCached(key, fetcher) {
  const cached = productsCache.get(key);

  if (cached) {
    if (cached.data) return Promise.resolve(cached.data);
    if (cached.promise) return cached.promise;
  }

  const promise = fetcher()
    .then((data) => {
      productsCache.set(key, { data });
      return data;
    })
    .catch((e) => {
      productsCache.delete(key);
      throw e;
    });

  productsCache.set(key, { promise });

  return promise;
}

export const clearProductsCache = () => {
  productsCache.clear();
};

registerProductCacheInvalidator(clearProductsCache);

const PRODUCT_CATEGORIES_STORAGE_KEY = 'products:categories';
const PRODUCT_CATEGORIES_STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

// "Selecione..." é o placeholder do formulário antigo gravado como linha de product_category,
// não uma categoria. O backend já o tira da lista, mas a cópia guardada aqui dura até 24h.
// Por nome, como no backend: o id dele não é o mesmo entre ambientes.
const PLACEHOLDER_CATEGORY_NAMES = ['Selecione...'];

const withoutPlaceholderCategories = (categories) =>
  Array.isArray(categories)
    ? categories.filter((category) => !PLACEHOLDER_CATEGORY_NAMES.includes(category?.name))
    : categories;

function readStoredProductCategories({ allowStale = false } = {}) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(PRODUCT_CATEGORIES_STORAGE_KEY);

    if (!rawValue) return null;

    const parsedValue = JSON.parse(rawValue);
    const isValidPayload =
      parsedValue &&
      Array.isArray(parsedValue.categories) &&
      typeof parsedValue.ts === 'number';

    if (!isValidPayload) {
      window.localStorage.removeItem(PRODUCT_CATEGORIES_STORAGE_KEY);
      return null;
    }

    const isFresh = Date.now() - parsedValue.ts < PRODUCT_CATEGORIES_STORAGE_TTL_MS;

    if (!allowStale && !isFresh) {
      return null;
    }

    return parsedValue.categories;
  } catch {
    return null;
  }
}

function writeStoredProductCategories(categories) {
  if (typeof window === 'undefined' || !window.localStorage || !Array.isArray(categories)) {
    return;
  }

  try {
    window.localStorage.setItem(
      PRODUCT_CATEGORIES_STORAGE_KEY,
      JSON.stringify({
        categories,
        ts: Date.now(),
      })
    );
  } catch {
    // Ignore storage failures and keep the in-memory path working.
  }
}

const createTimedCachedGetter = (fetcher, { ttlMs = 4 * 1000 } = {}) => {
  let cache = null;
  let ts = 0;
  let inflight = null;

  return async () => {
    const now = Date.now();

    if (cache !== null && now - ts < ttlMs) return cache;
    if (inflight) return inflight;

    inflight = fetcher()
      .then((data) => {
        cache = data;
        ts = Date.now();
        return data;
      })
      .finally(() => {
        inflight = null;
      });

    return inflight;
  };
};

export const getProductsService = async ({
  search = '',
  status = 'active',
  page = 1,
  limit = 10,
  type,
  currency,
  contentDeliveries,
  productionType,
  ordering,
  nfse,
}) => {
  const params = {
    page,
    search,
    limit,
    type,
    ...(currency && currency !== 'all' && { currency }),
    contentDeliveries,
    productionType,
    ...(ordering && { ordering }),
    ...(status !== 'all' && { status }),
    ...(nfse && nfse !== 'all' && { nfse }),
  };

  const key = `products:list:${buildKey(params)}`;

  return getCached(key, () =>
    AuthenticatedHttp.get('/products/', {
      params,
    }).then(({ data: { results, count } }) => ({
      products: results,
      count,
    }))
  );
};

/**
 * Uma página da listagem sem o cache da lista: para contagens que precisam do número de agora,
 * como o "N de M produtos emitem nota" da tela de NFS-e.
 */
export const countProductsService = async ({ status, nfse } = {}) =>
  AuthenticatedHttp.get('/products/', {
    params: { limit: 1, ...(status && { status }), ...(nfse && nfse !== 'all' && { nfse }) },
  }).then(({ data }) => ({ count: data?.count ?? 0, products: data?.results ?? [] }));

export const getProductsLookupService = async ({
  id,
  name,
  search,
  ordering,
  limit = 10,
  page = 1,
} = {}) =>
  AuthenticatedHttp.get('/products/lookup/', {
    params: {
      page,
      limit,
      ...(id && { id }),
      ...(name && { name }),
      ...(search && { search }),
      ...(ordering && { ordering }),
    },
  }).then(({ data: { results, count } }) => ({
    products: results,
    count,
  }));

export const getProductService = async ({ id }) =>
  AuthenticatedHttp.get(`/product/${id}/`).then(({ data: product }) => product);

export const getProductResumeService = async ({ id }) =>
  AuthenticatedHttp.get(`/product/${id}/resume/`).then(({ data: product }) => product);

// Sem credencial mesmo com sessão: o endpoint é AllowAny, mas o DRF valida o token antes da
// permissão, e um token vencido no localStorage vira 403 token_not_valid nesta rota pública.
export const getPublicProductService = async ({ id }) =>
  GuestHttp.get(`/product/${id}/public/`).then(({ data: product }) => product);

/**
 * Affiliate product detail uses affiliation-shaped fields (`productName`, nested `product.image`)
 * while producer product detail uses top-level `name` / `image`. Normalize so shared UI
 * (resume header, edit flows) can read a consistent shape without losing original keys.
 */
export const normalizeAffiliateProduct = (product) => {
  if (!product || typeof product !== 'object') return product;

  const nestedProduct = product.product && typeof product.product === 'object' ? product.product : null;

  return {
    ...product,
    name: product.name || product.productName || nestedProduct?.name || '',
    image: product.image || nestedProduct?.image || null,
  };
};

export const getAffiliateProductService = async ({ short_id }) =>
  AuthenticatedHttp.get(`affiliates/products/${short_id}/`).then(({ data: product }) =>
    normalizeAffiliateProduct(product)
  );

export const getProductCategoriesService = (() => {
  let cache = null;
  let inflight = null;

  return async () => {
    const storedCategories = readStoredProductCategories();

    if (cache) return cache;
    if (storedCategories?.length) {
      cache = withoutPlaceholderCategories(storedCategories);
      return cache;
    }
    if (inflight) return inflight;

    inflight = AuthenticatedHttp.get('/product/categories/')
      .then(({ data: { success, categories } }) => {
        if (!success) throw new Error('Erro ao buscar categorias');
        cache = withoutPlaceholderCategories(categories);
        writeStoredProductCategories(cache);
        inflight = null;
        return cache;
      })
      .catch((e) => {
        const staleCategories = readStoredProductCategories({ allowStale: true });
        inflight = null;
        if (staleCategories?.length) {
          cache = withoutPlaceholderCategories(staleCategories);
          return cache;
        }
        throw e;
      });

    return inflight;
  };
})();

export const createProductOffer = async ({ ...data }) =>
  AuthenticatedHttp.post('/offers/', { ...data })
    .then(({ data: offers }) => offers)
    .catch((error) => {
      throw new AppError(error);
    });

export const createProductService = async ({ ...info }) => {
  const result = await AuthenticatedHttp.post('/products/', {
    ...info,
  })
    .then(({ data: product }) => {
      invalidateAllProductCaches();
      return product;
    })
    .catch((error) => {
      throw new AppError(error);
    });

  if (info.type === 'subscription') {
    const product = await getProductService({ id: result.id });

    await updateProductService({
      ...product,
      id: product.id,
      offers: product.offers.map((offer) => {
        if (offer.default) {
          return {
            ...offer,
            type: 'subscription',
          };
        }
        return offer;
      }),
    });
  }

  return result;
};

export const updateProductService = async ({ id, ...info }) =>
  AuthenticatedHttp.put(`/product/${id}/`, info).then(({ data: product }) => {
    invalidateAllProductCaches();
    return product;
  });

export const updateProductOffers = async ({ id, ...info }) =>
  AuthenticatedHttp.put(`/offers/${id}`, info)
    .then(({ data: offers }) => offers)
    .catch((error) => {
      throw new AppError(error);
    });

export const updateAffiliatePixelsService = async ({ id, tracking_pixels }) =>
  AuthenticatedHttp.put(`/pixels/tracking_pixels/${tracking_pixels.id}/`, tracking_pixels).then(
    ({ data: product }) => product
  );

export const updateProductImageService = async ({ id, image }) => {
  const formData = new FormData();

  formData.append('image', image);

  return AuthenticatedHttp.put(`/product/${id}/image/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }).then(({ data }) => {
    if (data.detail) return data;
    throw new Error('Erro ao atualizar imagem do produto');
  });
};

export const deleteProductImageService = async ({ id }) =>
  AuthenticatedHttp.delete(`/product/${id}/image/`).then(({ data }) => {
    if (data.detail) return data;
    throw new Error('Erro ao deletar imagem do produto');
  });

export const deleteProductOffer = async ({ id }) => AuthenticatedHttp.delete(`/offers/${id}/`);

export const deleteProductService = async ({ id }) =>
  AuthenticatedHttp.delete(`/product/${id}/`).then(({ data }) => {
    invalidateAllProductCaches();
    return data;
  });

export const getPaymentMethods = createTimedCachedGetter(
  () =>
    AuthenticatedHttp.get('/products/payment_methods/')
      .then(({ data }) => data)
      .catch(() => []),
  {
    // Payment methods rarely change during an editing session.
    ttlMs: 30 * 60 * 1000,
  }
);

// A resposta agora varia por usuário (a entrega `cakto_v2` é gated). O cache é
// de módulo e não tem chave por conta — o TTL de 4s é curto o bastante para não
// vazar entre sessões, já que trocar de conta recarrega a aplicação. Se este TTL
// crescer, o cache precisa ser invalidado no logout.
export const getContentDeliveries = createTimedCachedGetter(
  () =>
    AuthenticatedHttp.get('/products/content_delivery/')
      .then(({ data }) => data)
      .catch(() => [])
);

export const getProductDeliveries = async (productId) =>
  AuthenticatedHttp.get(`/product/${productId}/delivery/`).then(({ data }) => data?.results);

export const createProductDelivery = async ({ id, type, username, password, ...rest }) =>
  AuthenticatedHttp.post(
    `/product/${id}/delivery/`,
    {
      type,
      username,
      password,
      ...rest,
    },
    { withCredentials: true }
  ).then(({ data }) => data);

export const createDeliveryAccessService = async ({ id, authCode, state }) =>
  AuthenticatedHttp.post(
    `/product/delivery_access/${id}/`,
    {
      authCode,
      state,
    },
    { withCredentials: true }
  ).then(({ data }) => data);

export const getDeliveryAccess = async () =>
  AuthenticatedHttp.get('/product/delivery_access/').then(({ data }) => data.results);

export const duplicateProductService = async (id) =>
  AuthenticatedHttp.post(`/product/${id}/duplicate/`).then(({ data }) => {
    invalidateAllProductCaches();
    return data;
  });

export const getShowcaseProducts = async (
  search,
  category,
  pageIndex = '',
  filters = {},
  sortBy = null
) => {
  const processedFilters = { ...filters };

  if (
    filters.affiliateCommission &&
    Array.isArray(filters.affiliateCommission) &&
    filters.affiliateCommission.length === 2
  ) {
    processedFilters.affiliateCommission__gte = parseFloat(filters.affiliateCommission[0]).toFixed(
      1
    );
    processedFilters.affiliateCommission__lte = parseFloat(filters.affiliateCommission[1]).toFixed(
      1
    );
    delete processedFilters.affiliateCommission;
  }

  if (filters.price && Array.isArray(filters.price) && filters.price.length === 2) {
    processedFilters.price__gte = filters.price[0];
    processedFilters.price__lte = filters.price[1];
    delete processedFilters.price;
  }

  return AuthenticatedHttp.get('/products/showcase/v2', {
    params: {
      ...(search && { search }),
      ...(category && { category }),
      ...(pageIndex && { page: pageIndex }),
      ...(sortBy && { ordering: `${sortBy.direction === 'desc' ? '-' : ''}${sortBy.field}` }),
      ...processedFilters,
    },
  }).then(({ data }) => data);
};

/**
 * @param {'click' | 'view' | 'affiliate'} type
 * @param {string[]} productIds
 * @returns {Promise<void>}
 */
export const registerMetricsForShowcaseProducts = async (type, productIds) =>
  AuthenticatedHttp.post('/products/showcase/register_events/', {
    type,
    products: productIds,
  });

export const listFunnelConversion = async (productId, startDate, endDate) => {
  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };

  return AuthenticatedHttp.get('/products/funnel', {
    params: {
      product_id: productId,
      ...(startDate && { start_date: formatDate(startDate) }),
      ...(endDate && { end_date: formatDate(endDate) }),
    },
  }).then(({ data }) => data);
};

/**
 * Liga ou desliga a nota fiscal das vendas de um produto, e grava na hora.
 *
 * O PUT do produto ignora `emitsNfse`: esta é a única porta de escrita fora da criação.
 * Desligar derruba junto a exigência de nota dos coprodutores (RN14); a resposta diz se
 * isso aconteceu, em `coproducerRequirementCleared`.
 */
export const updateProductNfseSetting = async ({ productId, emitsNfse }) =>
  AuthenticatedHttp.patch(`/product/${productId}/nfse-setting/`, { emitsNfse }).then(
    ({ data }) => data
  );

/**
 * O efeito de ligar ou desligar a nota em cada produto, sem gravar nada: assinantes que
 * renovam sem nota, coprodutores que deixam de ser obrigados e recusas por falta de cadastro.
 */
export const previewProductsNfseSetting = async ({ productIds, emitsNfse }) =>
  AuthenticatedHttp.post('/product/nfse-setting/preview/', {
    product_ids: productIds,
    emitsNfse,
  }).then(({ data }) => data);

/**
 * Aplica a escolha em vários produtos. O recusado (sem cadastro) volta em `refused`, e os
 * outros são aplicados; id que não é do produtor recusa o lote inteiro com 400 `notFound`.
 */
export const applyProductsNfseSetting = async ({ productIds, emitsNfse }) =>
  AuthenticatedHttp.post('/product/nfse-setting/bulk/', {
    product_ids: productIds,
    emitsNfse,
  }).then(({ data }) => data);

/**
 * Liga ou desliga a exigência de nota fiscal dos coprodutores do produto.
 *
 * A exigência vale no instante em que o pagamento é confirmado, não na venda (cakto-backend#3660):
 * ligar ou desligar alcança também Pix e boleto gerados antes e pagos depois, e não mexe na
 * pendência das vendas já pagas. Com a nota do produto desligada, ligar é recusado com 400
 * (RN12); desligar continua aceito.
 */
export const updateCoproducerInvoiceRequirement = async ({ productId, require }) =>
  AuthenticatedHttp.patch(`/product/${productId}/coproducer-invoice-requirement/`, {
    requireCoproducerInvoice: require,
  }).then(({ data }) => data);

/**
 * A situação fiscal de cada coprodutor do produto, na visão do produtor.
 *
 * Devolve `{ id, status }` por coprodução ativa, com `status` dizendo se aquele
 * coprodutor consegue emitir a nota devida. Só a situação, nunca dado fiscal:
 * o produtor precisa saber quem trava a cadeia, não o CNPJ de terceiro.
 */
export const getCoproducersFiscalStatus = async ({ productId }, options = {}) =>
  AuthenticatedHttp.get(`/product/${productId}/coproducers-fiscal-status/`, options).then(
    ({ data }) => data
  );
